import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";
import { GripVertical, Pin, RotateCcw, Settings2 } from "lucide-react";
import type { ColumnPin, ColumnPreference } from "./types";

interface ColumnDisplayPopoverProps {
  columns: ColumnPreference[];
  labels: Record<string, string>;
  /** Sr. No etc. — faded disabled checkbox, always shown in table */
  lockVisibilityIds?: Set<string>;
  onVisibleChange: (id: string, visible: boolean) => void;
  onAllVisibleChange: (visible: boolean) => void;
  onPinChange: (id: string, pin: ColumnPin) => void;
  onReorder: (activeId: string, overId: string) => void;
  onReset: () => void;
  className?: string;
}

function PinButton({
  active,
  side,
  onClick,
  title,
}: {
  active: boolean;
  side: "left" | "right";
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md transition-colors",
        active
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      <Pin
        className={cn(
          "h-3.5 w-3.5",
          side === "left" ? "-rotate-45" : "rotate-45"
        )}
      />
    </button>
  );
}

export function ColumnDisplayPopover({
  columns,
  labels,
  lockVisibilityIds,
  onVisibleChange,
  onAllVisibleChange,
  onPinChange,
  onReorder,
  onReset,
  className,
}: ColumnDisplayPopoverProps) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const toggleableColumns = useMemo(
    () => columns.filter(c => !lockVisibilityIds?.has(c.id)),
    [columns, lockVisibilityIds]
  );

  const allChecked = useMemo(
    () =>
      toggleableColumns.length > 0 && toggleableColumns.every(c => c.visible),
    [toggleableColumns]
  );

  const someChecked = useMemo(
    () => toggleableColumns.some(c => c.visible),
    [toggleableColumns]
  );

  const headerCheckboxState: boolean | "indeterminate" = allChecked
    ? true
    : someChecked
      ? "indeterminate"
      : false;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className={cn("h-8 w-8 shrink-0", className)}
          aria-label="Column display settings"
        >
          <Settings2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 rounded-xl p-0 shadow-lg"
        sideOffset={6}
      >
        <div className="flex items-center justify-between gap-2 border-b px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <Checkbox
              checked={headerCheckboxState}
              onCheckedChange={v => onAllVisibleChange(v === true)}
              className="h-5 w-5 shrink-0 rounded-full data-[state=checked]:border-primary data-[state=checked]:bg-primary"
              aria-label="Toggle all columns"
            />
            <span className="truncate font-semibold text-foreground">
              Column Display
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 gap-1 px-2 text-primary hover:text-primary"
            onClick={onReset}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </Button>
        </div>
        <ul className="max-h-72 overflow-y-auto py-1">
          {columns.map(col => {
            const lockVisibility = lockVisibilityIds?.has(col.id);
            const isDragOver = dragOverId === col.id && draggedId !== col.id;
            return (
              <li
                key={col.id}
                draggable
                onDragStart={() => setDraggedId(col.id)}
                onDragEnd={() => {
                  setDraggedId(null);
                  setDragOverId(null);
                }}
                onDragOver={e => {
                  e.preventDefault();
                  if (draggedId && draggedId !== col.id) {
                    setDragOverId(col.id);
                  }
                }}
                onDragLeave={() => {
                  if (dragOverId === col.id) {
                    setDragOverId(null);
                  }
                }}
                onDrop={e => {
                  e.preventDefault();
                  if (draggedId && draggedId !== col.id) {
                    onReorder(draggedId, col.id);
                  }
                  setDraggedId(null);
                  setDragOverId(null);
                }}
                className={cn(
                  "flex items-center gap-1.5 px-2 py-1.5 transition-colors",
                  isDragOver && "bg-primary/5",
                  draggedId === col.id && "opacity-50"
                )}
              >
                <button
                  type="button"
                  className="flex h-7 w-6 shrink-0 cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing"
                  aria-label={`Drag ${labels[col.id] ?? col.id}`}
                  onMouseDown={e => e.stopPropagation()}
                >
                  <GripVertical className="h-4 w-4" />
                </button>
                <Checkbox
                  checked={col.visible}
                  disabled={lockVisibility}
                  onCheckedChange={v => onVisibleChange(col.id, v === true)}
                  className={cn(
                    "h-5 w-5 shrink-0 rounded-full data-[state=checked]:border-primary data-[state=checked]:bg-primary",
                    lockVisibility &&
                      "opacity-60 disabled:cursor-default disabled:opacity-60"
                  )}
                  aria-label={`Toggle ${labels[col.id] ?? col.id}`}
                />
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate text-sm",
                    col.visible ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {labels[col.id] ?? col.id}
                </span>
                <div className="flex shrink-0 items-center gap-0.5">
                  <PinButton
                    side="left"
                    active={col.pin === "left"}
                    title="Pin to left"
                    onClick={() => onPinChange(col.id, "left")}
                  />
                  <PinButton
                    side="right"
                    active={col.pin === "right"}
                    title="Pin to right"
                    onClick={() => onPinChange(col.id, "right")}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
